import React, { createContext, useContext, useMemo, useEffect, useCallback } from 'react';
import { useColorScheme } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemeStore } from '../stores/themeStore';
import { lightPalette, darkPalette } from './palettes';
import { spacing, radius, shadows, FONT_SCALE_VALUES, createScaledTypography } from './tokens';
import type { Theme } from './types';

const ThemeContext = createContext<Theme | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const mode = useThemeStore((s) => s.mode);
  const fontSizeScale = useThemeStore((s) => s.fontSizeScale);
  const initialize = useThemeStore((s) => s.initialize);
  const systemScheme = useColorScheme();
  const insets = useSafeAreaInsets();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const theme = useMemo<Theme>(() => {
    const resolvedMode = mode === 'system'
      ? (systemScheme ?? 'light')
      : mode;

    const scaleValue = FONT_SCALE_VALUES[fontSizeScale];
    const scaledTypography = createScaledTypography(scaleValue);

    return {
      mode,
      colors: resolvedMode === 'dark' ? darkPalette : lightPalette,
      spacing,
      radius,
      shadows,
      typography: scaledTypography,
      fontScale: scaleValue,
      fs: (size: number) => Math.round(size * scaleValue),
      // Was a hardcoded 56 in every screen header. That is under the top inset
      // on Dynamic Island devices (59pt), so titles rendered behind the island,
      // and needlessly large on devices without a notch.
      headerPaddingTop: insets.top + spacing.md,
    };
  }, [mode, fontSizeScale, systemScheme, insets.top]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
